import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Loader,
  Paper,
  Container,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import {
  getAdminUploadSessions,
  guessAdminUploadSession,
  uploadAdminSession,
} from './api';


const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const TYPE_LABELS = {
  q_and_a: 'Q&A',
  accelerator: 'Accelerator',
  trade_room: 'Trade Room',
};

function canonicalFilename(sessionType, sessionDate) {
  const match = String(sessionDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match || !sessionType) {
    return '';
  }

  const [, year, monthText, dayText] = match;
  const month = MONTHS[Number(monthText) - 1];
  const day = Number(dayText);

  if (!month || !day) {
    return '';
  }

  if (sessionType === 'q_and_a') {
    return `Q&A ${month} ${day} ${year}.mp4`;
  }
  if (sessionType === 'accelerator') {
    return `TD Accelerator ${month} ${day} ${year}.mp4`;
  }
  if (sessionType === 'trade_room') {
    return `Trade Room ${month} ${day} ${year}.mp4`;
  }

  return '';
}

function statusColor(status) {
  if (status === 'active') {
    return 'green';
  }
  if (status === 'pending') {
    return 'yellow';
  }
  return 'red';
}

function formatDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return value || 'Unknown';
  }

  const [, year, month, day] = match;
  return `${Number(month)}/${Number(day)}/${year}`;
}

function groupSessions(rows) {
  const order = ['Q&A', 'Accelerator', 'Trade Room'];
  const groups = new Map(order.map((label) => [label, []]));

  for (const row of rows || []) {
    const label = row.session_type || 'Other';
    if (!groups.has(label)) {
      groups.set(label, []);
    }
    groups.get(label).push(row);
  }

  return Array.from(groups.entries())
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, rows: items }));
}

function StorageCard({ item }) {
  return (
    <Paper withBorder radius="md" p="md" style={{ flex: '1 1 240px' }}>
      <Stack gap={5}>
        <Group justify="space-between" align="center">
          <Text fw={800}>{item.label}</Text>
          <Badge variant="light">{item.raw_video_count} MP4{item.raw_video_count === 1 ? '' : 's'}</Badge>
        </Group>
        <Text size="xl" fw={800}>{item.raw_video_size}</Text>
        <Text size="xs" c="dimmed">{item.upload_dir}</Text>
        <Text size="xs" c={item.automation === 'Not scheduled' ? 'yellow.4' : 'dimmed'}>
          Import automation: {item.automation}
        </Text>
      </Stack>
    </Paper>
  );
}

export default function AdminUploadSessions() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [sessionType, setSessionType] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [guessMessage, setGuessMessage] = useState('');
  const [guessLoading, setGuessLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [accordionValues, setAccordionValues] = useState([]);

  const sessionTypeOptions = useMemo(() => {
    const fromServer = dashboard?.session_types || [];
    if (fromServer.length > 0) {
      return fromServer.map((item) => ({ value: item.value, label: item.label }));
    }

    return Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }));
  }, [dashboard]);

  const storedFilename = canonicalFilename(sessionType, sessionDate);
  const groupedSessions = useMemo(() => groupSessions(dashboard?.sessions || []), [dashboard]);
  const statusCounts = useMemo(() => {
    const counts = { active: 0, pending: 0, issue: 0 };
    for (const row of dashboard?.sessions || []) {
      if (Object.prototype.hasOwnProperty.call(counts, row.status)) {
        counts[row.status] += 1;
      }
    }
    return counts;
  }, [dashboard]);

  async function loadDashboard() {
    setLoading(true);
    setLoadError('');

    try {
      const data = await getAdminUploadSessions();
      setDashboard(data || null);
    } catch (err) {
      setLoadError(err.message || 'Failed to load upload administration data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setAccordionValues([]);
    loadDashboard();
  }, []);

  async function handleFileChange(file) {
    setSelectedFile(file || null);
    setUploadError('');
    setUploadSuccess('');
    setGuessMessage('');

    if (!file) {
      setSessionType('');
      setSessionDate('');
      return;
    }

    setGuessLoading(true);

    try {
      const guess = await guessAdminUploadSession({ filename: file.name });
      setSessionType(guess.session_type || '');
      setSessionDate(guess.session_date || '');

      if (guess.session_type && guess.session_date) {
        setGuessMessage(
          `Davealyzer recognized this as ${TYPE_LABELS[guess.session_type] || guess.session_type} ` +
          `for ${formatDate(guess.session_date)}. You can override either value before uploading.`
        );
      } else if (guess.session_type) {
        setGuessMessage(
          `Davealyzer recognized the session type as ${TYPE_LABELS[guess.session_type] || guess.session_type}, ` +
          'but could not determine the session date. Please enter it.'
        );
      } else if (guess.session_date) {
        setGuessMessage(
          `Davealyzer recognized the session date as ${formatDate(guess.session_date)}, ` +
          'but could not determine the session type. Please select it.'
        );
      } else {
        setGuessMessage('Davealyzer could not infer the session type or date from this filename. Please select them manually.');
      }
    } catch (err) {
      setGuessMessage('Filename guessing was unavailable. Select the session type and date manually.');
    } finally {
      setGuessLoading(false);
    }
  }

  async function handleUpload() {
    if (!selectedFile || !sessionType || !sessionDate) {
      setUploadError('Choose an MP4, session type, and session date before uploading.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    setUploadSuccess('');

    try {
      const result = await uploadAdminSession({
        file: selectedFile,
        sessionType,
        sessionDate,
        onProgress: setUploadProgress,
      });

      setUploadProgress(100);
      setUploadSuccess(
        `${result.stored_filename || storedFilename} was uploaded successfully to ` +
        `${result.upload_dir || 'the session upload directory'} (${result.size || 'size unavailable'}).`
      );
      setSelectedFile(null);
      setSessionType('');
      setSessionDate('');
      setGuessMessage('');
      await loadDashboard();
    } catch (err) {
      setUploadError(err.message || 'The upload failed.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center" wrap="wrap">
          <Button component="a" href="/" variant="subtle" px={0}>
            ← Back to Davealyzer
          </Button>
          <Badge color="green" variant="light">Administrator</Badge>
        </Group>

        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={2}>
            <Title order={2}>Upload Sessions</Title>
            <Text size="sm" c="dimmed">Davealyzer session administration</Text>
            <Text size="sm" c="dimmed">
              Upload new session videos and review whether each session is active, pending import, or has an import issue.
            </Text>
          </Stack>

          <Group gap="xs">
            <Badge color="green" variant="light">Active {statusCounts.active}</Badge>
            <Badge color="yellow" variant="light">Pending {statusCounts.pending}</Badge>
            <Badge color="red" variant="light">Issues {statusCounts.issue}</Badge>
          </Group>
        </Group>

        <Paper withBorder radius="md" p="md">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Text fw={800} size="lg">Upload a new session</Text>
                <Text size="sm" c="dimmed">
                  Davealyzer guesses the type and date from the source filename, but you remain in control of both values.
                </Text>
              </div>
            </Group>

            <FileInput
              label="Session video"
              description="MP4 only. The original source filename is used for guessing; Davealyzer stores the video under the canonical importer filename."
              placeholder="Choose an MP4 session video"
              accept="video/mp4,.mp4"
              value={selectedFile}
              onChange={handleFileChange}
              disabled={uploading}
              clearable
            />

            <Group grow align="flex-start">
              <Select
                label="Session type"
                placeholder="Select session type"
                data={sessionTypeOptions}
                value={sessionType}
                onChange={(value) => setSessionType(value || '')}
                disabled={uploading}
                allowDeselect={false}
              />

              <TextInput
                label="Session date"
                type="date"
                value={sessionDate}
                onChange={(event) => setSessionDate(event.currentTarget.value)}
                disabled={uploading}
              />
            </Group>

            {guessLoading && (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">Examining filename…</Text>
              </Group>
            )}

            {!guessLoading && guessMessage && (
              <Alert color="blue" title="Filename guess">
                {guessMessage}
              </Alert>
            )}

            {storedFilename && (
              <Paper withBorder radius="sm" p="sm">
                <Text size="xs" fw={700} c="dimmed">FILE THAT WILL BE STORED</Text>
                <Text fw={700}>{storedFilename}</Text>
                <Text size="xs" c="dimmed" mt={3}>
                  This canonical name is intentionally used so the existing importer recognizes the session reliably.
                </Text>
              </Paper>
            )}

            {uploadError && (
              <Alert color="red" title="Upload failed">{uploadError}</Alert>
            )}

            {uploadSuccess && (
              <Alert color="green" title="Upload complete">{uploadSuccess}</Alert>
            )}

            {uploading && (
              <Stack gap={5}>
                <Group justify="space-between">
                  <Text size="sm" fw={700}>Uploading video…</Text>
                  <Text size="sm">{uploadProgress}%</Text>
                </Group>
                <Progress value={uploadProgress} animated={uploadProgress < 100} />
                <Text size="xs" c="dimmed">
                  Keep this window open until the upload finishes. The importer cannot see the video until the complete file has been safely published.
                </Text>
              </Stack>
            )}

            <Group justify="flex-end">
              <Button
                onClick={handleUpload}
                loading={uploading}
                disabled={!selectedFile || !sessionType || !sessionDate || guessLoading}
              >
                Upload Session
              </Button>
            </Group>
          </Stack>
        </Paper>

        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <div>
              <Text fw={800} size="lg">Raw video storage</Text>
              <Text size="sm" c="dimmed">Current MP4 count and disk consumption in each active upload directory.</Text>
            </div>
            <Button size="sm" variant="light" onClick={loadDashboard} loading={loading}>
              Refresh
            </Button>
          </Group>

          {loadError && (
            <Alert color="red" title="Could not load administration data">{loadError}</Alert>
          )}

          {loading && !dashboard ? (
            <Group justify="center" py="lg"><Loader /></Group>
          ) : (
            <Group align="stretch" grow>
              {(dashboard?.storage || []).map((item) => (
                <StorageCard key={item.key} item={item} />
              ))}
            </Group>
          )}
        </Stack>

        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <div>
              <Text fw={800} size="lg">Session status</Text>
              <Text size="sm" c="dimmed">
                Active sessions are fully imported with a completed browse summary. Pending sessions have raw video awaiting import. Issues require attention.
                {dashboard?.generated_at ? ` Generated ${dashboard.generated_at}.` : ''}
              </Text>
            </div>
          </Group>

          {loading && dashboard ? (
            <Group justify="center" py="lg"><Loader /></Group>
          ) : (dashboard?.sessions || []).length === 0 ? (
            <Alert color="yellow" title="No sessions found">
              No Q&A, Accelerator, or Trade Room sessions were found.
            </Alert>
          ) : (
            <Accordion
              multiple
              variant="separated"
              value={accordionValues}
              onChange={setAccordionValues}
            >
              {groupedSessions.map((group) => {
                const pending = group.rows.filter((row) => row.status === 'pending').length;
                const issues = group.rows.filter((row) => row.status === 'issue').length;

                return (
                  <Accordion.Item key={group.label} value={group.label}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="wrap" pr="sm">
                        <Text fw={800}>{group.label}</Text>
                        <Group gap={6}>
                          <Badge variant="light">{group.rows.length} sessions</Badge>
                          {pending > 0 && <Badge color="yellow" variant="light">{pending} pending</Badge>}
                          {issues > 0 && <Badge color="red" variant="light">{issues} issue{issues === 1 ? '' : 's'}</Badge>}
                        </Group>
                      </Group>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                        <Table striped highlightOnHover verticalSpacing="sm">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ width: 120 }}>Date</Table.Th>
                              <Table.Th>Session</Table.Th>
                              <Table.Th style={{ width: 110 }}>Status</Table.Th>
                              <Table.Th>Detail</Table.Th>
                              <Table.Th>Raw video</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {group.rows.map((row) => (
                              <Table.Tr
                                key={row.key}
                                style={{
                                  backgroundColor: row.status === 'issue'
                                    ? 'rgba(250, 82, 82, 0.15)'
                                    : row.status === 'pending'
                                      ? 'rgba(250, 176, 5, 0.10)'
                                      : undefined,
                                }}
                              >
                                <Table.Td><Text fw={600}>{formatDate(row.session_date)}</Text></Table.Td>
                                <Table.Td>
                                  <Text fw={700}>{row.title}</Text>
                                  {row.session_id && <Text size="xs" c="dimmed">Session ID {row.session_id}</Text>}
                                </Table.Td>
                                <Table.Td>
                                  <Badge color={statusColor(row.status)} variant="light">
                                    {row.status_label}
                                  </Badge>
                                </Table.Td>
                                <Table.Td><Text size="sm">{row.detail}</Text></Table.Td>
                                <Table.Td>
                                  <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                                    {row.raw_file || 'Not present'}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Paper>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}
